import { useEffect, useState } from 'react';
import { useAuth } from '../auth/AuthContext';
import { useRepositories } from '../../../infrastructure/http/RepositoryProvider';
import {
  getExistingPushSubscription,
  pushUnsupportedReason,
  subscribeToPush,
  unsubscribeFromPush,
} from '../../../infrastructure/browser/webPush';
import { canUseFingerprintLogin, guessDeviceName } from '../../../infrastructure/browser/webauthnSupport';
import type { WebAuthnCredentialSummary } from '../../ports/AuthRepository';

export function useSettingsPage() {
  const { user, updateName, logout, registerPasskey } = useAuth();
  const { auth, push } = useRepositories();

  const [showRename, setShowRename] = useState(false);
  const [renameError, setRenameError] = useState<string | null>(null);
  const [pushUnsupported, setPushUnsupported] = useState<string | null>(null);
  const [pushEnabled, setPushEnabled] = useState(false);
  const [pushBusy, setPushBusy] = useState(false);
  const [pushError, setPushError] = useState<string | null>(null);
  const [passkeySupported, setPasskeySupported] = useState(false);
  const [passkeys, setPasskeys] = useState<WebAuthnCredentialSummary[]>([]);
  const [passkeyBusy, setPasskeyBusy] = useState(false);
  const [passkeyError, setPasskeyError] = useState<string | null>(null);

  const loadPasskeys = () => {
    auth.listWebAuthnCredentials().then(setPasskeys).catch(() => undefined);
  };

  useEffect(() => {
    canUseFingerprintLogin().then(setPasskeySupported);
    loadPasskeys();
  }, []);

  const handleEnrollPasskey = async () => {
    setPasskeyBusy(true);
    setPasskeyError(null);
    const result = await registerPasskey(guessDeviceName());
    setPasskeyBusy(false);
    if (!result.ok) {
      setPasskeyError(result.error ?? 'No se pudo activar la huella en este dispositivo.');
      return;
    }
    loadPasskeys();
  };

  const handleRemovePasskey = async (id: string) => {
    setPasskeyError(null);
    try {
      await auth.deleteWebAuthnCredential(id);
      loadPasskeys();
    } catch (err) {
      setPasskeyError(err instanceof Error ? err.message : 'No se pudo quitar la huella.');
    }
  };

  useEffect(() => {
    const reason = pushUnsupportedReason();
    if (reason) {
      setPushUnsupported(reason);
      return;
    }
    getExistingPushSubscription()
      .then((sub) => setPushEnabled(!!sub))
      .catch(() => undefined);
  }, []);

  const handleTogglePush = async (checked: boolean) => {
    setPushBusy(true);
    setPushError(null);
    try {
      if (checked) {
        const sub = await subscribeToPush();
        await push.registerSubscription(sub.toJSON() as { endpoint: string; keys: { p256dh: string; auth: string } });
        setPushEnabled(true);
      } else {
        const sub = await unsubscribeFromPush();
        if (sub) {
          await push.unregisterSubscription(sub.endpoint).catch(() => undefined);
        }
        setPushEnabled(false);
      }
    } catch (err) {
      if (checked) setPushEnabled(false);
      setPushError(err instanceof Error ? err.message : 'No se pudo actualizar las notificaciones.');
    } finally {
      setPushBusy(false);
    }
  };

  const handleRename = async (name: string) => {
    if (!name.trim()) {
      setRenameError('El nombre no puede estar vacío.');
      return;
    }
    const result = await updateName(name.trim());
    if (!result.ok) {
      setRenameError(result.error ?? 'No se pudo actualizar el nombre.');
    }
  };

  return {
    user,
    logout,
    showRename,
    setShowRename,
    renameError,
    setRenameError,
    pushUnsupported,
    pushEnabled,
    pushBusy,
    pushError,
    handleTogglePush,
    handleRename,
    passkeySupported,
    passkeys,
    passkeyBusy,
    passkeyError,
    handleEnrollPasskey,
    handleRemovePasskey,
  };
}
